import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
  Badge,
  InputAdornment,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { getTodayBrasilia, getTomorrowBrasilia, isToday, isTomorrow, formatDateBR, formatDateTimeBR, extractDateYYYYMMDD, getCurrentMonthYearBrasilia } from '../utils/dateUtils';

type PeriodType = 'month' | 'day';

interface PriorityOrder {
  id: string;
  contato: string;
  numeroPedido: string;
  dataEntrega: string;
  categoria: string;
  frete: string;
  material: string;
  status: string;
  localizacao: string;
  observacoes: string;
  motivo: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA' | 'URGENTE';
  clienteId: string;
  createdAt: string;
  updatedAt: string;
  entregaHoje?: boolean;
  entregaAmanha?: boolean;
}

interface NewOrder {
  contato: string;
  numeroPedido: string;
  dataEntrega: string;
  categoria: string;
  frete: string;
  material: string;
  status: string;
  localizacao: string;
  observacoes: string;
  motivo: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA' | 'URGENTE';
  clienteId: string;
}

const PriorityControl: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const today = new Date().toISOString().slice(0, 10);
  const [orders, setOrders] = useState<PriorityOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('');
  const [prioridadeFilter, setPrioridadeFilter] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PriorityOrder | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [dateFilter, setDateFilter] = useState('');

  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [month, setMonth] = useState(currentMonth.toString());
  const [year, setYear] = useState(currentYear.toString());
  const [selectedDate, setSelectedDate] = useState(today);

  // Estados para seleção múltipla e exclusão
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<'single' | 'multiple'>('single');
  const [orderToDeleteId, setOrderToDeleteId] = useState<string | null>(null);

  // Estados para o modal de adicionar nova prioridade
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newOrderData, setNewOrderData] = useState<Partial<PriorityOrder>>({
    contato: '',
    numeroPedido: '',
    dataEntrega: '',
    categoria: 'REENVIO',
    frete: '',
    status: 'AGUARDANDO',
    localizacao: '',
    observacoes: '',
    motivo: '',
    prioridade: 'MEDIA',
    clienteId: '',
  });

  // Verifica se o usuário atual é admin
  const canEdit = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPPORT' || currentUser?.role === 'MANAGER' || currentUser?.role === 'PRODUCAO';

  const getDateRange = (): { startDate: string; endDate: string } => {
    if (periodType === 'day') {
      return { startDate: selectedDate, endDate: selectedDate };
    }
    const m = parseInt(month);
    const y = parseInt(year);
    const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const endDate = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  };

  // Função para retornar o indicativo visual de entrega (HOJE/AMANHÃ)
  // Usa helpers do dateUtils que respeitam timezone de Brasília
  const getDeliveryIndicator = (dateString: string) => {
    if (isToday(dateString)) {
      return { label: 'HOJE', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
    } else if (isTomorrow(dateString)) {
      return { label: 'AMANHÃ', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)' };
    }
    return null;
  };

  // Status possíveis
  const statusOptions = [
    'DESIGNER',
    'RIPAGEM',
    'IMPRESSÃO',
    'SECAGEM',
    'CORTE',
    'RECORTE',
    'FINALIZADO',
    'CANCELADO',
    'AGUARDANDO',
  ];

  // Categorias possíveis
  const categoriaOptions = [
    'REENVIO',
    'ANTECIPAR PEDIDO',
    'PAC',
    'SEDEX',
  ];

  // Prioridades possíveis
  const prioridadeOptions = [
    { value: 'BAIXA', label: 'Baixa', color: '#10b981' },
    { value: 'MEDIA', label: 'Média', color: '#f59e0b' },
    { value: 'ALTA', label: 'Alta', color: '#ef4444' },
    { value: 'URGENTE', label: 'Urgente', color: '#dc2626' },
  ];

  // Carregar pedidos da API
  const loadOrders = async () => {
    try {
      setLoading(true);
      // Buscar dados da API (salvos no banco)
      const apiOrders = await apiService.getAllOrders();
      
      // Mapeamento reverso: PICKUP -> Retirada, SEDEX -> SEDEX, etc
      const reverseShippingMethodMap: { [key: string]: string } = {
        'PICKUP': 'Retirada',
        'SEDEX': 'Sedex',
        'PAC': 'Pac',
        'TOTAL_EXPRESS': 'Total'
      };

      // Mapeamento reverso: DESIGNER -> DESIGNER, RIPAGEM -> RIPAGEM, etc
      const reverseStatusMap: { [key: string]: string } = {
        'DESIGNER': 'DESIGNER',
        'RIPAGEM': 'RIPAGEM',
        'IMPRESSOR': 'IMPRESSÃO',
        'SECANDO': 'SECAGEM',
        'CORTE': 'CORTE',
        'RECORTE': 'RECORTE',
        'FINALIZED': 'FINALIZADO',
        'CANCELLED': 'CANCELADO',
        'PENDING': 'AGUARDANDO'
      };
      
      // Transformar dados da API para o formato do frontend
      const transformedOrders: PriorityOrder[] = apiOrders.map((order: any) => {
        return {
          id: order.id,
          contato: order.client?.phone || 'N/A',
          numeroPedido: order.orderNumber,
          dataEntrega: order.deliveryDate ? extractDateYYYYMMDD(order.deliveryDate) : '',
          categoria: order.categoria || 'REENVIO',
          frete: order.freight || '',
          material: reverseShippingMethodMap[order.shippingMethod] || order.shippingMethod || '-',
          status: reverseStatusMap[order.status] || order.status,
          localizacao: order.trackingCode || '-',
          observacoes: order.observations || '',
          motivo: order.reason || '',
          prioridade: order.priority === 'LOW' ? 'BAIXA' : 
                     order.priority === 'MEDIUM' ? 'MEDIA' :
                     order.priority === 'HIGH' ? 'ALTA' : 'URGENTE',
          clienteId: order.clientId,
          createdAt: order.createdAt,
          updatedAt: order.updatedAt,
        };
      });
      
      setOrders(transformedOrders);
      setSnackbar({
        open: true,
        message: '✅ Prioridades carregadas do banco de dados',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao carregar pedidos:', error);
      // Fallback para mock data se a API falhar
      const mockOrders: PriorityOrder[] = [
        {
          id: '1',
          contato: '(11)9554-7341',
          numeroPedido: '18436',
          dataEntrega: getTodayBrasilia(),
          categoria: 'REENVIO',
          frete: '0',
          material: 'SEDEX',
          status: 'FINALIZADO',
          localizacao: 'A8550323807BR',
          observacoes: 'Extravio Total',
          motivo: 'Pedido Extraviado',
          prioridade: 'ALTA',
          clienteId: 'client-1',
          createdAt: '2025-01-01',
          updatedAt: '2025-01-01',
        },
        {
          id: '2',
          contato: '(65)99918-1978',
          numeroPedido: '18690',
          dataEntrega: getTomorrowBrasilia(),
          categoria: 'REENVIO',
          frete: '0',
          material: 'PAC',
          status: 'IMPRESSÃO',
          localizacao: 'AM9946747909BR',
          observacoes: 'Pedido em produção',
          motivo: 'Cliente solicitou urgência',
          prioridade: 'ALTA',
          clienteId: 'client-2',
          createdAt: '2025-01-01',
          updatedAt: '2025-01-01',
        },
        {
          id: '3',
          contato: '(62)99934-0997',
          numeroPedido: '18697',
          dataEntrega: getTomorrowBrasilia(),
          categoria: 'ANTECIPAR PEDIDO',
          frete: '0',
          material: 'SEDEX',
          status: 'CORTE',
          localizacao: 'AM9944815855BR',
          observacoes: 'Aguardando corte',
          motivo: 'Material chegou hoje',
          prioridade: 'MEDIA',
          clienteId: 'client-3',
          createdAt: '2025-01-01',
          updatedAt: '2025-01-01',
        },
      ];
      setOrders(mockOrders);
      setSnackbar({
        open: true,
        message: '⚠️ Carregando dados do cache (banco indisponível)',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [periodType, month, year, selectedDate]);

  // Filtrar pedidos baseado nos filtros aplicados
  const filteredOrders = orders.filter(order => {
    const matchesSearch = searchTerm === '' ||
      order.contato.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.numeroPedido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.observacoes.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === '' || order.status === statusFilter;
    const matchesCategoria = categoriaFilter === '' || order.categoria === categoriaFilter;
    const matchesPrioridade = prioridadeFilter === '' || order.prioridade === prioridadeFilter;

    // Filtro por data de entrega (usando helpers com timezone de Brasília)
    const matchesDate = dateFilter === '' || (() => {
      if (dateFilter === 'hoje') {
        return isToday(order.dataEntrega);
      } else if (dateFilter === 'amanha') {
        return isTomorrow(order.dataEntrega);
      }
      return true;
    })();

    // Filtro por período (createdAt)
    const { startDate, endDate } = getDateRange();
    const orderDate = order.createdAt ? order.createdAt.slice(0, 10) : '';
    const matchesPeriod = orderDate >= startDate && orderDate <= endDate;

    // Pedidos FINALIZADO ficam ocultos por padrão.
    // Só aparecem se o usuário buscar pelo número do pedido ou filtrar por status "FINALIZADO"
    const isFinalized = order.status === 'FINALIZADO';
    const isSearchingSpecificOrder = searchTerm !== '' && order.numeroPedido.toLowerCase().includes(searchTerm.toLowerCase());
    const isFilteringFinalized = statusFilter === 'FINALIZADO';
    const showFinalized = !isFinalized || isSearchingSpecificOrder || isFilteringFinalized;

    return matchesSearch && matchesStatus && matchesCategoria && matchesPrioridade && matchesDate && matchesPeriod && showFinalized;
  });


  const handleUpdateOrder = async () => {
    if (!editingOrder) return;

    try {
      // Mapear prioridade do frontend para backend
      const priorityMap: { [key: string]: string } = {
        'BAIXA': 'LOW',
        'MEDIA': 'MEDIUM',
        'ALTA': 'HIGH',
        'URGENTE': 'URGENT'
      };

      // Mapear status do frontend para backend
      const statusMap: { [key: string]: string } = {
        'DESIGNER': 'DESIGNER',
        'RIPAGEM': 'RIPAGEM',
        'IMPRESSÃO': 'IMPRESSOR',
        'SECAGEM': 'SECANDO',
        'CORTE': 'CORTE',
        'RECORTE': 'RECORTE',
        'FINALIZADO': 'FINALIZED',
        'CANCELADO': 'CANCELLED',
        'AGUARDANDO': 'PENDING'
      };

      // Mapear shipping method do frontend para backend
      const shippingMethodMap: { [key: string]: string } = {
        'Retirada': 'PICKUP',
        'RETIRADA': 'PICKUP',
        'Sedex': 'SEDEX',
        'SEDEX': 'SEDEX',
        'Pac': 'PAC',
        'PAC': 'PAC',
        'Total': 'TOTAL_EXPRESS',
        'TOTAL': 'TOTAL_EXPRESS',
        'TOTAL_EXPRESS': 'TOTAL_EXPRESS'
      };

      // Atualizar pedido via API (Prisma) - SALVA NO BANCO
      const updatedApiOrder = await apiService.updateOrder(editingOrder.id, {
        orderNumber: editingOrder.numeroPedido,
        clientPhone: editingOrder.contato,
        status: statusMap[editingOrder.status],
        priority: priorityMap[editingOrder.prioridade],
        deliveryDate: editingOrder.dataEntrega,
        shippingMethod: shippingMethodMap[editingOrder.frete || 'RETIRADA'] || 'PICKUP',
        freight: editingOrder.frete,
        categoria: editingOrder.categoria,
        observations: editingOrder.observacoes,
        reason: editingOrder.motivo,
      });

      // Transformar resposta para formato do frontend
      const updatedOrder: PriorityOrder = {
        ...editingOrder,
        id: editingOrder.id,
        contato: editingOrder.contato,
        numeroPedido: editingOrder.numeroPedido,
        status: updatedApiOrder.status,
        prioridade: editingOrder.prioridade,
        dataEntrega: extractDateYYYYMMDD(updatedApiOrder.deliveryDate),
        categoria: editingOrder.categoria,
        frete: editingOrder.frete,
        material: updatedApiOrder.shippingMethod || editingOrder.material,
        localizacao: editingOrder.localizacao,
        observacoes: updatedApiOrder.observations || '',
        motivo: updatedApiOrder.reason || '',
        clienteId: editingOrder.clienteId,
        createdAt: editingOrder.createdAt,
        updatedAt: updatedApiOrder.updatedAt,
      };

      const updatedOrders = orders.map(order =>
        order.id === editingOrder.id ? updatedOrder : order
      );
      setOrders(updatedOrders);
      setEditingOrder(null);
      setOpenEditDialog(false);

      setSnackbar({
        open: true,
        message: '✅ Prioridade atualizada no banco de dados com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar pedido: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      // Mapear status do frontend para backend
      const statusMap: { [key: string]: string } = {
        'DESIGNER': 'DESIGNER',
        'RIPAGEM': 'RIPAGEM',
        'IMPRESSÃO': 'IMPRESSOR',
        'SECAGEM': 'SECANDO',
        'CORTE': 'CORTE',
        'RECORTE': 'RECORTE',
        'FINALIZADO': 'FINALIZED',
        'CANCELADO': 'CANCELLED',
        'AGUARDANDO': 'PENDING'
      };

      // Atualizar no banco via API
      await apiService.updateOrder(orderId, {
        status: statusMap[newStatus] || newStatus
      });

      // Recarregar os dados para garantir que tudo está sincronizado
      await loadOrders();

      setSnackbar({
        open: true,
        message: '✅ Status atualizado com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar status: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'DESIGNER': return '#8b5cf6';
      case 'RIPAGEM': return '#f59e0b';
      case 'IMPRESSÃO': return '#3b82f6';
      case 'SECAGEM': return '#06b6d4';
      case 'CORTE': return '#ec4899';
      case 'RECORTE': return '#10b981';
      case 'FINALIZADO': return '#10b981';
      case 'CANCELADO': return '#ef4444';
      case 'AGUARDANDO': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPrioridadeColor = (prioridade: string) => {
    const prioridadeObj = prioridadeOptions.find(p => p.value === prioridade);
    return prioridadeObj?.color || '#6b7280';
  };

  const getCategoriaColor = (categoria: string) => {
    switch (categoria.toUpperCase()) {
      case 'REENVIO': return { bg: 'rgba(59, 130, 246, 0.1)', text: '#3b82f6' };
      case 'ANTECIPAR PEDIDO': return { bg: 'rgba(249, 115, 22, 0.1)', text: '#f97316' };
      default: return { bg: 'rgba(212, 175, 55, 0.1)', text: '#D4AF37' };
    }
  };

  // Função para adicionar nova prioridade (Admin only) - SALVA NO BANCO
  const handleAddOrder = async () => {
    if (!newOrderData.contato || !newOrderData.numeroPedido || !newOrderData.dataEntrega) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha os campos obrigatórios: Contato, Número do Pedido e Data de Entrega',
        severity: 'error',
      });
      return;
    }

    try {
      // Mapear prioridade do frontend para backend
      const priorityMap: { [key: string]: string } = {
        'BAIXA': 'LOW',
        'MEDIA': 'MEDIUM',
        'ALTA': 'HIGH',
        'URGENTE': 'URGENT'
      };

      // Mapear status do frontend para backend
      const statusMap: { [key: string]: string } = {
        'DESIGNER': 'DESIGNER',
        'RIPAGEM': 'RIPAGEM',
        'IMPRESSÃO': 'IMPRESSOR',
        'SECAGEM': 'SECANDO',
        'CORTE': 'CORTE',
        'RECORTE': 'RECORTE',
        'FINALIZADO': 'FINALIZED',
        'CANCELADO': 'CANCELLED',
        'AGUARDANDO': 'PENDING'
      };

      // Mapear shipping method do frontend para backend
      const shippingMethodMap: { [key: string]: string } = {
        'Retirada': 'PICKUP',
        'RETIRADA': 'PICKUP',
        'Sedex': 'SEDEX',
        'SEDEX': 'SEDEX',
        'Pac': 'PAC',
        'PAC': 'PAC',
        'Total': 'TOTAL_EXPRESS',
        'TOTAL': 'TOTAL_EXPRESS',
        'TOTAL_EXPRESS': 'TOTAL_EXPRESS'
      };

      // Mapear razão (motivo/categoria) do frontend para backend
      const reasonMap: { [key: string]: string } = {
        'REENVIO': 'REENVIO',
        'ANTECIPAR PEDIDO': 'ANTECIPAR PEDIDO'
      };

      // Criar pedido via API (Prisma) - SALVA NO BANCO
      const createdOrder = await apiService.createOrder({
        orderNumber: newOrderData.numeroPedido || '',
        clientId: currentUser?.id || '',
        sellerId: currentUser?.id || '',
        deliveryDate: newOrderData.dataEntrega || '',
        shippingMethod: shippingMethodMap[newOrderData.frete || 'RETIRADA'] || 'PICKUP',
        contato: newOrderData.contato || '',
        status: statusMap[newOrderData.status || 'AGUARDANDO'],
        priority: priorityMap[newOrderData.prioridade as string || 'MEDIA'],
        categoria: newOrderData.categoria || 'REENVIO',
        observations: newOrderData.observacoes,
        reason: newOrderData.motivo || '',
        freight: newOrderData.frete || '0',
      });

      // Transformar resposta para formato do frontend
      const newOrder: PriorityOrder = {
        id: createdOrder.id,
        contato: newOrderData.contato || '',
        numeroPedido: createdOrder.orderNumber,
        dataEntrega: extractDateYYYYMMDD(createdOrder.deliveryDate),
        categoria: newOrderData.categoria || 'REENVIO',
        frete: (createdOrder.freight || '') as string,
        material: (createdOrder.shippingMethod || '') as string,
        status: createdOrder.status as string,
        localizacao: (createdOrder.trackingCode || '') as string,
        observacoes: (createdOrder.observations || '') as string,
        motivo: (createdOrder.reason || '') as string,
        prioridade: (newOrderData.prioridade as 'BAIXA' | 'MEDIA' | 'ALTA' | 'URGENTE') || 'MEDIA',
        clienteId: createdOrder.clientId,
        createdAt: createdOrder.createdAt,
        updatedAt: createdOrder.updatedAt,
      };

      setOrders([...orders, newOrder]);
      
      // Limpar o formulário
      setNewOrderData({
        contato: '',
        numeroPedido: '',
        dataEntrega: '',
        categoria: 'REENVIO',
        frete: '',
        status: 'AGUARDANDO',
        localizacao: '',
        observacoes: '',
        motivo: '',
        prioridade: 'MEDIA',
        clienteId: '',
      });
      
      setOpenAddDialog(false);
      setSnackbar({
        open: true,
        message: '✅ Prioridade salva no banco de dados com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao adicionar prioridade: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  // Funções de seleção para deletar
  const handleSelectOrder = (orderId: string) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(order => order.id)));
    }
  };

  // Função para deletar prioridades
  const handleDeleteOrders = async () => {
    try {
      if (deleteTarget === 'single' && orderToDeleteId) {
        // Deletar do banco via API (Prisma)
        await apiService.deleteOrder(orderToDeleteId);
        const updatedOrders = orders.filter(order => order.id !== orderToDeleteId);
        setOrders(updatedOrders);
        setSnackbar({
          open: true,
          message: '✅ Prioridade deletada do banco de dados com sucesso!',
          severity: 'success',
        });
      } else if (deleteTarget === 'multiple' && selectedOrders.size > 0) {
        // Deletar múltiplas do banco via API (Prisma)
        const idsArray = Array.from(selectedOrders);
        await apiService.deleteMultipleOrders(idsArray);
        
        const updatedOrders = orders.filter(order => !selectedOrders.has(order.id));
        setOrders(updatedOrders);
        setSelectedOrders(new Set());
        setSnackbar({
          open: true,
          message: `✅ ${selectedOrders.size} prioridade(s) deletada(s) do banco de dados com sucesso!`,
          severity: 'success',
        });
      }
      setOpenDeleteDialog(false);
      setOrderToDeleteId(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao deletar prioridade: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            Controle de Prioridades
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Gerencie pedidos e acompanhe o fluxo de produção
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              label="Período"
            >
              <MenuItem value="month">Mês inteiro</MenuItem>
              <MenuItem value="day">Dia específico</MenuItem>
            </Select>
          </FormControl>

          {periodType === 'month' && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mês</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mês">
                  <MenuItem value="1">Janeiro</MenuItem>
                  <MenuItem value="2">Fevereiro</MenuItem>
                  <MenuItem value="3">Março</MenuItem>
                  <MenuItem value="4">Abril</MenuItem>
                  <MenuItem value="5">Maio</MenuItem>
                  <MenuItem value="6">Junho</MenuItem>
                  <MenuItem value="7">Julho</MenuItem>
                  <MenuItem value="8">Agosto</MenuItem>
                  <MenuItem value="9">Setembro</MenuItem>
                  <MenuItem value="10">Outubro</MenuItem>
                  <MenuItem value="11">Novembro</MenuItem>
                  <MenuItem value="12">Dezembro</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Ano">
                  <MenuItem value="2023">2023</MenuItem>
                  <MenuItem value="2024">2024</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {periodType === 'day' && (
            <TextField
              size="small"
              label="Data"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}

          {canEdit && (
            <Tooltip title="Adicionar nova prioridade (Admin)">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDialog(true)}
                sx={{
                  backgroundColor: '#D4AF37',
                  color: '#1a1a1a',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#c49927',
                  },
                }}
              >
                Adicionar Prioridade
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Buscar por contato, pedido ou observações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={categoriaFilter}
                  label="Categoria"
                  onChange={(e) => setCategoriaFilter(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categoriaOptions.map(categoria => (
                    <MenuItem key={categoria} value={categoria}>{categoria}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={prioridadeFilter}
                  label="Prioridade"
                  onChange={(e) => setPrioridadeFilter(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {prioridadeOptions.map(prioridade => (
                    <MenuItem key={prioridade.value} value={prioridade.value}>
                      {prioridade.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Data de Entrega</InputLabel>
                <Select
                  value={dateFilter}
                  label="Data de Entrega"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="hoje">🟢 Hoje</MenuItem>
                  <MenuItem value="amanha">🟠 Amanhã</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadOrders}
                  sx={{ height: '56px', flex: 1 }}
                >
                  Atualizar
                </Button>
                {canEdit && selectedOrders.size > 0 && (
                  <Tooltip title={`Deletar ${selectedOrders.size} prioridade(s) selecionada(s)`}>
                    <Button
                      variant="contained"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setDeleteTarget('multiple');
                        setOpenDeleteDialog(true);
                      }}
                      sx={{
                        height: '56px',
                        backgroundColor: '#ef4444',
                        color: '#fff',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#dc2626',
                        },
                      }}
                    >
                      Deletar ({selectedOrders.size})
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0, m: 0 }}>
          <TableContainer sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
            <Table sx={{ width: '100%', tableLayout: 'auto' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)', height: 70, '& > th': { py: 3 } }}>
                  {canEdit && (
                    <TableCell sx={{ width: 50, textAlign: 'center' }}>
                      <Checkbox
                        checked={selectedOrders.size === filteredOrders.length && filteredOrders.length > 0}
                        indeterminate={selectedOrders.size > 0 && selectedOrders.size < filteredOrders.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>CONTATO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>N° PEDIDO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>DATA DE ENTREGA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>CATEGORIA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#D4AF37", px: 1, flex: 1 }}>FRETE</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>STATUS/LOCALIZAÇÃO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>OBSERVAÇÕES</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>MOTIVO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>PRIORIDADE</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>DATA DE ADIÇÃO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>AÇÕES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 12 : 11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Carregando pedidos...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 12 : 11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Nenhum pedido encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{
                        backgroundColor: isToday(order.dataEntrega) ? '#e0f2f7' : isTomorrow(order.dataEntrega) ? '#fffbeb' : 'transparent',
                        transition: 'background-color 0.2s ease-in-out',
                      }}
                    >
                      {canEdit && (
                        <TableCell sx={{ width: 50, textAlign: 'center' }}>
                          <Checkbox
                            checked={selectedOrders.has(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ color: '#6b7280', fontSize: 16 }} />
                          {order.contato}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, px: 1, flex: 1 }}>{order.numeroPedido}</TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                            {order.dataEntrega}
                          </Typography>
                          {getDeliveryIndicator(order.dataEntrega) && (
                            <Chip
                              label={getDeliveryIndicator(order.dataEntrega)?.label}
                              size="small"
                              sx={{
                                backgroundColor: getDeliveryIndicator(order.dataEntrega)?.bgColor,
                                color: getDeliveryIndicator(order.dataEntrega)?.color,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Chip
                          label={order.categoria}
                          size="small"
                          sx={{
                            backgroundColor: getCategoriaColor(order.categoria).bg,
                            color: getCategoriaColor(order.categoria).text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                          {order.frete}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={order.status}
                              onChange={(e) => handleStatusChange(order.id, e.target.value)}
                              sx={{
                                '& .MuiSelect-select': {
                                  color: getStatusColor(order.status),
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                },
                              }}
                            >
                              {statusOptions.map(status => (
                                <MenuItem key={status} value={status}>{status}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.observacoes}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.motivo}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={`Prioridade: ${order.prioridade}`}>
                            <Chip
                              label={order.prioridade}
                              size="small"
                              sx={{
                                backgroundColor: `${getPrioridadeColor(order.prioridade)}20`,
                                color: getPrioridadeColor(order.prioridade),
                                fontWeight: 600,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {formatDateTimeBR(order.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {canEdit && (
                            <>
                              <Tooltip title="Editar prioridade">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingOrder(order);
                                    setOpenEditDialog(true);
                                  }}
                                  sx={{ color: '#3b82f6' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Deletar prioridade">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setOrderToDeleteId(order.id);
                                    setDeleteTarget('single');
                                    setOpenDeleteDialog(true);
                                  }}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>


      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        sx={{ width: '100%' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog para Adicionar Prioridade */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a1a', py: 3 }}>
          ➕ Adicionar Nova Prioridade
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {/* Contato */}
          <TextField
            label="Contato"
            fullWidth
            value={newOrderData.contato || ''}
            onChange={(e) => setNewOrderData({ ...newOrderData, contato: e.target.value })}
            placeholder="Ex: João Silva"
            required
            variant="outlined"
            size="small"
          />

          {/* Número do Pedido */}
          <TextField
            label="Número do Pedido"
            fullWidth
            value={newOrderData.numeroPedido || ''}
            onChange={(e) => setNewOrderData({ ...newOrderData, numeroPedido: e.target.value })}
            placeholder="Ex: #12345"
            required
            variant="outlined"
            size="small"
          />

          {/* Data de Entrega */}
          <TextField
            label="Data de Entrega"
            type="date"
            fullWidth
            value={newOrderData.dataEntrega || ''}
            onChange={(e) => setNewOrderData({ ...newOrderData, dataEntrega: e.target.value })}
            required
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          {/* Categoria */}
          <FormControl fullWidth size="small">
            <InputLabel>Categoria</InputLabel>
            <Select
              value={newOrderData.categoria || 'REENVIO'}
              label="Categoria"
              onChange={(e) => setNewOrderData({ ...newOrderData, categoria: e.target.value })}
            >
              <MenuItem value="REENVIO">REENVIO</MenuItem>
              <MenuItem value="ANTECIPAR PEDIDO">ANTECIPAR PEDIDO</MenuItem>
            </Select>
          </FormControl>

          {/* Material/Frete */}
          <TextField
            label="Frete"
            fullWidth
            value={newOrderData.frete || ''}
            onChange={(e) => setNewOrderData({ ...newOrderData, frete: e.target.value })}
            placeholder="Ex: RETIRADA, SEDEX, PAC, etc"
            variant="outlined"
            size="small"
          />

          {/* Status */}
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={newOrderData.status || 'AGUARDANDO'}
              label="Status"
              onChange={(e) => setNewOrderData({ ...newOrderData, status: e.target.value })}
            >
              {statusOptions.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Prioridade */}
          <FormControl fullWidth size="small">
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={newOrderData.prioridade || 'MEDIA'}
              label="Prioridade"
              onChange={(e) => setNewOrderData({ ...newOrderData, prioridade: e.target.value as any })}
            >
              <MenuItem value="BAIXA">BAIXA</MenuItem>
              <MenuItem value="MEDIA">MEDIA</MenuItem>
              <MenuItem value="ALTA">ALTA</MenuItem>
              <MenuItem value="URGENTE">URGENTE</MenuItem>
            </Select>
          </FormControl>

          {/* Observações */}
          <TextField
            label="Observações"
            fullWidth
            value={newOrderData.observacoes || ''}
            onChange={(e) => setNewOrderData({ ...newOrderData, observacoes: e.target.value })}
            placeholder="Digite observações..."
            multiline
            rows={3}
            variant="outlined"
            size="small"
          />

          {/* Motivo */}
          <TextField
            label="Motivo"
            fullWidth
            value={newOrderData.motivo || ''}
            onChange={(e) => setNewOrderData({ ...newOrderData, motivo: e.target.value })}
            placeholder="Digite o motivo..."
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenAddDialog(false)}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{ color: '#6b7280' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddOrder}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#D4AF37',
              color: '#1a1a1a',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#c49927',
              },
            }}
          >
            Salvar Prioridade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Editar Prioridade */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a1a', py: 3 }}>
          ✏️ Editar Prioridade
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {editingOrder && (
            <>
              {/* Contato */}
              <TextField
                label="Contato"
                fullWidth
                value={editingOrder.contato || ''}
                onChange={(e) => setEditingOrder({ ...editingOrder, contato: e.target.value })}
                variant="outlined"
                size="small"
              />

              {/* Número do Pedido */}
              <TextField
                label="Número do Pedido"
                fullWidth
                value={editingOrder.numeroPedido || ''}
                onChange={(e) => setEditingOrder({ ...editingOrder, numeroPedido: e.target.value })}
                variant="outlined"
                size="small"
              />

              {/* Data de Entrega */}
              <TextField
                label="Data de Entrega"
                type="date"
                fullWidth
                value={editingOrder.dataEntrega || ''}
                onChange={(e) => setEditingOrder({ ...editingOrder, dataEntrega: e.target.value })}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              {/* Categoria */}
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={editingOrder.categoria || 'REENVIO'}
                  label="Categoria"
                  onChange={(e) => setEditingOrder({ ...editingOrder, categoria: e.target.value })}
                >
                  <MenuItem value="REENVIO">REENVIO</MenuItem>
                  <MenuItem value="ANTECIPAR PEDIDO">ANTECIPAR PEDIDO</MenuItem>
                </Select>
              </FormControl>

              {/* Material/Frete */}
              <TextField
                label="Frete"
                fullWidth
                value={editingOrder.frete || ''}
                onChange={(e) => setEditingOrder({ ...editingOrder, frete: e.target.value })}
                placeholder="Ex: RETIRADA, SEDEX, PAC, etc"
                variant="outlined"
                size="small"
              />

              {/* Status */}
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editingOrder.status || 'AGUARDANDO'}
                  label="Status"
                  onChange={(e) => setEditingOrder({ ...editingOrder, status: e.target.value })}
                >
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Prioridade */}
              <FormControl fullWidth size="small">
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={editingOrder.prioridade || 'MEDIA'}
                  label="Prioridade"
                  onChange={(e) => setEditingOrder({ ...editingOrder, prioridade: e.target.value as any })}
                >
                  <MenuItem value="BAIXA">BAIXA</MenuItem>
                  <MenuItem value="MEDIA">MEDIA</MenuItem>
                  <MenuItem value="ALTA">ALTA</MenuItem>
                  <MenuItem value="URGENTE">URGENTE</MenuItem>
                </Select>
              </FormControl>

              {/* Observações */}
              <TextField
                label="Observações"
                fullWidth
                value={editingOrder.observacoes || ''}
                onChange={(e) => setEditingOrder({ ...editingOrder, observacoes: e.target.value })}
                multiline
                rows={3}
                variant="outlined"
                size="small"
              />

              {/* Motivo */}
              <TextField
                label="Motivo"
                fullWidth
                value={editingOrder.motivo || ''}
                onChange={(e) => setEditingOrder({ ...editingOrder, motivo: e.target.value })}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setEditingOrder(null);
            }}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{ color: '#6b7280' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateOrder}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#D4AF37',
              color: '#1a1a1a',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#c49927',
              },
            }}
          >
            Salvar Alterações
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Deletar */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a1a', py: 3 }}>
          ⚠️ Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: '#6b7280' }}>
            {deleteTarget === 'single'
              ? 'Tem certeza que deseja deletar esta prioridade? Esta ação não pode ser desfeita.'
              : `Tem certeza que deseja deletar ${selectedOrders.size} prioridade(s)? Esta ação não pode ser desfeita.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{ color: '#6b7280' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteOrders}
            variant="contained"
            sx={{
              backgroundColor: '#ef4444',
              color: '#fff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#dc2626',
              },
            }}
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriorityControl;





























